import { resolveId } from '@medplum/core'
import type {
  Appointment,
  Communication,
  MedicationRequest,
  Reference,
  ServiceRequest,
  Task,
} from '@medplum/fhirtypes'
import { formatDateTime } from '@seen/core/dates'
import { referenceDisplay } from '@seen/core/display'
import * as appointments from '@seen/core/models/appointments/index'
import { CommentCommunication } from '@seen/core/models/communications/CommentCommunication'
import { ExtendedMedicationRequest } from '@seen/core/models/medicationRequest'
import { Order } from '@seen/core/models/orders/Order'
import { SDR } from '@seen/core/models/tasks/sdr/sdr'
import { StandardTask } from '@seen/core/models/tasks/standard/standardTask'
import { hasReference, isOfType } from '@seen/core/utils'
import { Button, cn, DotSeparator, TypographyDetail } from '@seen/ui'
import { IconLink, IconPencil } from '@tabler/icons-react'
import { Fragment, type JSX, useRef, useState } from 'react'
import { toast } from 'sonner'
import { CommentEditor } from '#src/components/activity/CommentEditor.tsx'
import { CommentObservations } from '#src/components/activity/CommentObservations.tsx'
import { CommentRelatedTasks } from '#src/components/activity/CommentRelatedTasks.tsx'
import { CommentReply } from '#src/components/activity/CommentReply.tsx'
import { DropdownMenuNewTaskFromCommentItem } from '#src/components/activity/DropdownMenuNewTaskFromCommentItem.tsx'
import { EditHistorySheet } from '#src/components/activity/EditHistorySheet.tsx'
import { ReplyComposer } from '#src/components/activity/ReplyComposer.tsx'
import { useCommentCommunicationActivity } from '#src/components/activity/UseCommentCommunicationActivity.tsx'
import { useComposeTaskFromComment } from '#src/components/activity/useComposeTaskFromComment.ts'
import { NoteSourceBadge } from '#src/components/clinical/NoteSourceBadge.tsx'
import { Avatar } from '#src/components/core/Avatar/Avatar.tsx'
import { Card } from '#src/components/core/Card.tsx'
import { DropdownMenuDeleteItem } from '#src/components/core/dropdown-menu/DropdownMenuDeleteItem.tsx'
import { DropdownMenuItem } from '#src/components/core/dropdown-menu/DropdownMenuItem.tsx'
import { HoverDate } from '#src/components/core/hover-card/HoverDate.tsx'
import { HoverPractitioner } from '#src/components/core/hover-card/HoverPractitioner.tsx'
import { TextLink } from '#src/components/core/TextLink.tsx'
import { DropdownMenuMedplumLinkItem } from '#src/components/DropdownMenuMedplumLinkItem.tsx'
import { HoverTask } from '#src/components/HoverTask.tsx'
import { MoreDropdownMenu } from '#src/components/MoreDropdownMenu.tsx'
import type { RichTextEditorRef } from '#src/components/rich-text-editor/RichTextEditor.tsx'
import { RichText } from '#src/components/RichText.tsx'
import { TaskableRichText } from '#src/components/TaskableRichText.tsx'
import { Hotkeys } from '#src/constants/hotkeys.ts'
import { useCurrentPractitioner } from '#src/hooks/useCurrentPractitioner.ts'
import { useDidChange } from '#src/hooks/useDidChange.ts'
import { useFhirRead } from '#src/hooks/useFhirRead.ts'
import { useTimelineItemHashMatch } from '#src/hooks/useHashMatch.ts'
import { useHotkeys } from '#src/hooks/useHotkeys.ts'
import { useInMemoryStore } from '#src/hooks/useInMemoryStore.ts'
import { useIsReadOnly } from '#src/hooks/useIsReadOnly.ts'
import { useLinkOrigin } from '#src/hooks/useLinkOrigin.ts'
import { usePatchMutation } from '#src/hooks/usePatchMutation.ts'
import { useRead } from '#src/hooks/useRead.ts'
import { useScrollHighlight } from '#src/hooks/useScrollHighlight.ts'
import { useSearch } from '#src/hooks/useSearch.ts'
import { useSearchOne } from '#src/hooks/useSearchOne.ts'
import { useTimelineItemUrl } from '#src/hooks/useTimelineItemUrl.ts'
import { buildAppointmentRelativeUrl } from '#src/stores/calendar.ts'
import { extractTextFromHTML } from '#src/utils/html.ts'

interface ActivityCommentProps {
  comment: CommentCommunication
  dateComponent?: (date: string | Date) => JSX.Element
  forceShowOptions?: boolean
  showRelated?: boolean
}

export function ActivityComment({
  comment,
  dateComponent = (date) => <HoverDate date={date} format="datetime" />,
  forceShowOptions = false,
  showRelated = false,
}: ActivityCommentProps): JSX.Element {
  const linkOrigin = useLinkOrigin()
  const currentPractitioner = useCurrentPractitioner()
  const isReadOnly = useIsReadOnly()
  const mutateComment = usePatchMutation<CommentCommunication>().mutate
  const [editing, setEditing] = useState(false)

  const related = comment.partOf?.[0]

  const { data: parentComment } = useSearchOne(
    CommentCommunication,
    {
      _id: comment.parentComment ? resolveId(comment.parentComment) : undefined,
    },
    { query: { enabled: !!comment.parentComment } }
  )

  const { data: replies } = useSearch(CommentCommunication, {
    ...CommentCommunication.searchQuery([comment.reference]),
    _sort: 'sent',
  })

  const hasReplies = (replies?.length ?? 0) > 0
  const [showReplyComposer, setShowReplyComposer] = useState(hasReplies)
  const didHasRepliesChange = useDidChange(hasReplies)
  if (didHasRepliesChange) {
    setShowReplyComposer(hasReplies)
  }

  const replyEditorRef = useRef<RichTextEditorRef>(null)

  const isEdited = useCommentCommunicationActivity(comment).length > 1

  const elementId = `comment-${comment.id}`
  const highlightCommentId = useInMemoryStore((s) => s.highlightCommentId)
  const hashMatch = useTimelineItemHashMatch(comment.id)
  const { ref: highlightRef, highlightClassName } = useScrollHighlight(
    highlightCommentId === elementId || hashMatch
  )

  const [hovering, setHovering] = useState(false)
  const composeTaskFromComment = useComposeTaskFromComment(comment)

  const isGloballyShowingTaskableTextButton = useInMemoryStore(
    (state) => state.isShowingTaskableTextButton
  )
  useHotkeys(Hotkeys.NewTaskFromText, () => composeTaskFromComment(), {
    enabled: hovering && !isGloballyShowingTaskableTextButton,
  })

  const isCommentAuthor = hasReference(
    comment.sender,
    currentPractitioner.reference
  )

  const commentUrl = useTimelineItemUrl(comment)

  return (
    <div
      className={cn(
        '-mx-1.5',
        'z-10' // The component should appear above the line in ActivityList
      )}
    >
      <Card className="p-0">
        <div
          id={elementId}
          ref={highlightRef}
          className={cn(
            'group flex flex-col gap-1.5 rounded-lg p-4',
            (!!replies?.length || showReplyComposer) && 'rounded-b-none',
            highlightClassName
          )}
          onMouseEnter={() => setHovering(true)}
          onMouseLeave={() => setHovering(false)}
          role="none"
        >
          <div className="flex justify-between gap-2">
            <div className="flex items-center gap-2">
              <Avatar profile={comment.sender} size="icon" />
              <TypographyDetail className="flex items-center truncate">
                {comment.practitionerSender ? (
                  <HoverPractitioner
                    practitioner={comment.practitionerSender}
                    className="mr-1 truncate"
                  />
                ) : (
                  <span className="mr-1 truncate font-medium">
                    {referenceDisplay(comment.sender)}
                  </span>
                )}
                <span className="font-normal text-foreground-secondary">
                  {related && showRelated && (
                    <RelatedResourceDisplay related={related} />
                  )}
                  <span className="font-normal text-nowrap">
                    <DotSeparator />
                    {dateComponent(comment.sent)}
                    {isEdited && (
                      <>
                        {' '}
                        <EditHistorySheet comment={comment}>
                          (edited)
                        </EditHistorySheet>
                      </>
                    )}
                  </span>
                </span>
              </TypographyDetail>

              <NoteSourceBadge comment={comment} />
            </div>
            {!editing && (
              <div className="-mr-2.5 flex">
                <Button
                  variant="ghost"
                  size="icon"
                  className={cn(
                    '-my-2.75 invisible py-2.75 group-hover:visible',
                    forceShowOptions && 'visible'
                  )}
                  onClick={() => {
                    void navigator.clipboard.writeText(
                      `${linkOrigin()}${commentUrl}`
                    )
                    toast.info('Comment URL copied to clipboard')
                  }}
                >
                  <IconLink className="size-4" />
                </Button>
                {!isReadOnly && (
                  <>
                    <Button
                      variant="ghost"
                      size="icon"
                      className={cn(
                        '-my-2.75 invisible py-2.75 group-hover:visible',
                        forceShowOptions && 'visible'
                      )}
                      onClick={() => {
                        setShowReplyComposer(true)
                        replyEditorRef.current?.focus()
                        // Since the reply composer is hidden, we need to request an animation frame to ensure it gets focused.
                        requestAnimationFrame(() => {
                          replyEditorRef.current?.focus()
                        })
                      }}
                    >
                      <IconReply className="size-4" />
                    </Button>
                    <MoreDropdownMenu
                      className={cn(
                        'invisible group-hover:visible data-[state=open]:visible',
                        forceShowOptions && 'visible'
                      )}
                    >
                      {isCommentAuthor && (
                        <DropdownMenuItem
                          className="flex items-center gap-2"
                          onSelect={() => setEditing(true)}
                        >
                          <IconPencil className="-mt-px size-4 text-foreground-secondary" />
                          Edit
                        </DropdownMenuItem>
                      )}
                      <DropdownMenuNewTaskFromCommentItem comment={comment} />
                      <DropdownMenuDeleteItem
                        deleteConfirmationProps={{
                          title: `Delete comment?`,
                          description: (
                            <div className="flex flex-col gap-4 select-none">
                              <Card>
                                <RichText
                                  value={comment.message}
                                  className="text-sm"
                                />
                              </Card>
                              <span>
                                Deleted comments can still be recovered by
                                contacting our team.
                              </span>
                            </div>
                          ),
                          onConfirm: () => {
                            mutateComment({
                              instance: comment,
                              mutateFn: (c) => {
                                c.markDeleted()
                              },
                            })
                          },
                        }}
                      />
                      <DropdownMenuMedplumLinkItem to={comment} />
                    </MoreDropdownMenu>
                  </>
                )}
              </div>
            )}
          </div>
          {!editing && (
            <div>
              <TaskableRichText
                value={comment.message}
                className="text-sm"
                onSelectCreateTask={composeTaskFromComment}
              />
              <CommentRelatedTasks comment={comment} />
              <CommentObservations payload={comment.payload} />
            </div>
          )}
          {editing && (
            <CommentEditor
              comment={comment}
              onClose={() => setEditing(false)}
            />
          )}
        </div>
        {!!replies?.length && (
          <div className="border-t">
            {replies.map((r, idx) => (
              <Fragment key={r.reference.reference}>
                {idx > 0 && <div className="ml-11 border-t" />}
                <CommentReply comment={r} dateComponent={dateComponent} />
              </Fragment>
            ))}
          </div>
        )}
        {showReplyComposer && !isReadOnly && (
          <div className="border-t px-4 py-2.5">
            <ReplyComposer
              comment={
                comment.parentComment && parentComment ? parentComment : comment
              }
              editorRef={replyEditorRef}
            />
          </div>
        )}
      </Card>
    </div>
  )
}

function IconReply({ className }: { className?: string }): JSX.Element {
  // Icon from linear.app
  return (
    <svg
      className={className}
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="currentColor"
      role="img"
      focusable="false"
      aria-hidden="true"
      xmlns="http://www.w3.org/2000/svg"
    >
      <path d="M5.137 3.118a.518.518 0 0 1 .56-.063c.184.089.3.277.301.483v1.926C14.392 5.822 15 12.336 15 12.427a.534.534 0 0 1-.487.537h-.043a.533.533 0 0 1-.524-.45c-.01-.054-1.412-3.788-7.947-4.05l.002 2a.529.529 0 0 1-.887.397l-3.95-3.548a.54.54 0 0 1 .026-.816l3.948-3.379Z"></path>
    </svg>
  )
}

function ResourceReferenceLink({
  reference,
}: {
  reference: Reference
}): JSX.Element | null {
  if (isOfType(reference, 'Task')) {
    return <TaskLink task={reference} />
  }
  if (isOfType(reference, 'ServiceRequest')) {
    return <OrderLink order={reference} />
  }
  if (isOfType(reference, 'Appointment')) {
    return <AppointmentLink appointment={reference} />
  }
  if (isOfType(reference, 'Communication')) {
    return <CommentReferenceLink comment={reference} />
  }
  if (isOfType(reference, 'MedicationRequest')) {
    return <MedicationRequestLink medicationRequest={reference} />
  }
  return null
}

function CommentReferenceLink({
  comment,
}: {
  comment: CommentCommunication | Reference<Communication>
}): JSX.Element | null {
  const commentData = (comment as any).message
    ? (comment as CommentCommunication)
    : undefined
  const commentRef = (comment as any).reference
    ? (comment as Reference<Communication>)
    : undefined
  const { data } = useRead(CommentCommunication, commentRef)
  const commentObj = commentData ?? data
  const commentUrl = useTimelineItemUrl(commentObj)
  const related = commentObj?.partOf?.[0]
  if (!commentObj || !commentUrl) {
    return null
  }
  return (
    <span className="inline-flex items-center gap-1">
      replied to
      <TextLink
        to={commentUrl}
        className="inline-block max-w-xs truncate text-ellipsis"
      >
        {extractTextFromHTML(commentObj.message)}
      </TextLink>
      {related && !isOfType(related, 'Patient') && (
        <>
          {' on '}
          <ResourceReferenceLink reference={related} />
        </>
      )}
    </span>
  )
}

function TaskLink({ task }: { task: Reference<Task> }): JSX.Element | null {
  const { data } = useFhirRead('Task', resolveId(task))
  if (!data) {
    return null
  }
  if (StandardTask.isInstance(data)) {
    return <HoverTask task={StandardTask.fromFhirResource(data)} />
  } else if (SDR.isInstance(data)) {
    const sdr = SDR.fromFhirResource(data)
    return (
      <TextLink
        to={`/Patient/${resolveId(sdr.participant)}/sdr/${resolveId(data)}`}
      >
        {sdr.title}
      </TextLink>
    )
  }
  return null
}

function OrderLink({
  order,
}: {
  order: Reference<ServiceRequest>
}): JSX.Element | null {
  const { data } = useRead(Order, order)

  const participant = data?.participant
  if (!participant) {
    return null
  }

  return (
    <TextLink
      to={`/Patient/${resolveId(participant)}/orders/${resolveId(order)}`}
      className="max-w-xs truncate text-ellipsis"
    >
      {data?.titleWithId ?? referenceDisplay(order)}
    </TextLink>
  )
}

function AppointmentLink({
  appointment,
}: {
  appointment: Reference<Appointment>
}): JSX.Element | null {
  const { data } = useRead(appointments.ExtendedAppointment, appointment)
  if (!data) {
    return null
  }
  return (
    <TextLink to={buildAppointmentRelativeUrl(data)}>
      {data.title}
      {data.start && (
        <>
          {' @ '}
          {formatDateTime(data.start)}
        </>
      )}
    </TextLink>
  )
}

function MedicationRequestLink({
  medicationRequest,
}: {
  medicationRequest: Reference<MedicationRequest>
}): JSX.Element | null {
  const { data } = useRead(ExtendedMedicationRequest, medicationRequest)
  if (!data) {
    return null
  }

  const subject = data.subject
  if (!subject) {
    return null
  }

  return (
    <TextLink
      to={`/Patient/${resolveId(subject)}/medications/${resolveId(medicationRequest)}`}
    >
      {data.medicationDisplay}
    </TextLink>
  )
}

function RelatedResourceDisplay({
  related,
}: {
  related: Reference
}): JSX.Element | null {
  if (isOfType(related, 'Communication')) {
    return <ResourceReferenceLink reference={related} />
  }
  if (
    isOfType(related, 'Task') ||
    isOfType(related, 'ServiceRequest') ||
    isOfType(related, 'Appointment') ||
    isOfType(related, 'MedicationRequest')
  ) {
    return (
      <>
        <span className="text-nowrap">commented on</span>
        &nbsp;
        <ResourceReferenceLink reference={related} />
      </>
    )
  }
  return null
}
